import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';

function formatError(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

/**
 * Full-page sign-in / sign-up. Replaces the previous modal implementation
 * because modals on mobile (especially inside the Emergent preview sheet
 * and iOS Safari) routinely clipped or hid the form. A real page is the
 * most reliable way to give the form room to breathe on every device.
 *
 * Routes:
 *   /signin  -> opens with the "Sign in" tab
 *   /signup  -> opens with the "Create account" tab
 */
export default function AuthPage({ initialTab }) {
  const navigate = useNavigate();
  const params = useParams();
  const { login, register, user } = useAuth();

  // Tab can come from the prop (route default) or via the page query
  const startingTab = initialTab || params.tab || 'login';
  const [tab, setTab] = useState(startingTab);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Sign in
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Sign up
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  // Forgot password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  // If they're already signed in, take them straight to their saved readings
  useEffect(() => {
    if (user) navigate('/saved', { replace: true });
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      navigate('/saved');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(regEmail, regPassword, regName);
      navigate('/saved');
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchTab = (next) => {
    setTab(next);
    setError('');
  };

  return (
    <div className="auth-page" data-testid="auth-page">
      <div className="auth-page-inner">
        <button
          type="button"
          className="auth-page-back"
          onClick={() => navigate(-1)}
          data-testid="auth-back-btn"
          aria-label="Back"
        >
          <span aria-hidden="true">←</span>
          <span>Back</span>
        </button>

        <div className="auth-page-card" data-testid="auth-page-card">
          <div className="auth-page-heading">
            <h1>{tab === 'login' ? 'Welcome back' : tab === 'register' ? 'Create your account' : 'Reset your password'}</h1>
            <p>
              {tab === 'login' && 'Sign in to see your saved readings and family blueprints.'}
              {tab === 'register' && 'Save your readings, build a family profile, and unlock the full experience.'}
              {tab === 'forgot' && "Pop in your email and we'll send you a link to set a new password."}
            </p>
          </div>

          {tab !== 'forgot' && (
            <div className="auth-tabs">
              <button
                type="button"
                className={`auth-tab ${tab === 'login' ? 'active' : ''}`}
                onClick={() => switchTab('login')}
                data-testid="login-tab"
              >
                Sign in
              </button>
              <button
                type="button"
                className={`auth-tab ${tab === 'register' ? 'active' : ''}`}
                onClick={() => switchTab('register')}
                data-testid="register-tab"
              >
                Create account
              </button>
            </div>
          )}

          {error && <div className="auth-error" data-testid="auth-error">{error}</div>}

          {tab === 'login' && (
            <form onSubmit={handleLogin} data-testid="login-form">
              <label className="field-label">Email</label>
              <input
                type="email"
                className="field-input"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
                data-testid="login-email"
              />
              <label className="field-label">Password</label>
              <input
                type="password"
                className="field-input"
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                placeholder="Your password"
                required
                data-testid="login-password"
              />
              <button type="submit" className="auth-submit" disabled={loading} data-testid="login-submit">
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
              <div className="auth-page-secondary">
                <button type="button" className="auth-link" onClick={() => switchTab('forgot')} data-testid="forgot-link">
                  Forgot your password?
                </button>
              </div>
            </form>
          )}

          {tab === 'register' && (
            <form onSubmit={handleRegister} data-testid="register-form">
              <label className="field-label">Your name</label>
              <input
                type="text"
                className="field-input"
                value={regName}
                onChange={e => setRegName(e.target.value)}
                placeholder="First name"
                required
                autoFocus
                data-testid="register-name"
              />
              <label className="field-label">Email</label>
              <input
                type="email"
                className="field-input"
                value={regEmail}
                onChange={e => setRegEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="register-email"
              />
              <label className="field-label">Password</label>
              <input
                type="password"
                className="field-input"
                value={regPassword}
                onChange={e => setRegPassword(e.target.value)}
                placeholder="At least 6 characters"
                required
                minLength={6}
                data-testid="register-password"
              />
              <button type="submit" className="auth-submit" disabled={loading} data-testid="register-submit">
                {loading ? 'Creating…' : 'Create account'}
              </button>
              <p className="auth-fineprint">
                By creating an account you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.
              </p>
            </form>
          )}

          {tab === 'forgot' && (
            <>
              {forgotSent ? (
                <div className="auth-success" data-testid="forgot-success">
                  <strong>Check your inbox.</strong>
                  <p>If an account exists for {forgotEmail}, you'll receive an email with a link to reset your password.</p>
                  <button type="button" className="auth-link" onClick={() => switchTab('login')} data-testid="back-to-login">
                    ← Back to sign in
                  </button>
                </div>
              ) : (
                <form onSubmit={handleForgot} data-testid="forgot-form">
                  <label className="field-label">Email</label>
                  <input
                    type="email"
                    className="field-input"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    autoFocus
                    data-testid="forgot-email"
                  />
                  <button type="submit" className="auth-submit" disabled={loading} data-testid="forgot-submit">
                    {loading ? 'Sending…' : 'Send reset link'}
                  </button>
                  <div className="auth-page-secondary">
                    <button type="button" className="auth-link" onClick={() => switchTab('login')} data-testid="back-to-login-link">
                      ← Back to sign in
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
